// 2026-07-29 - FormuLearning - 클릭형 레이어 팝오버 (이슈 #51)
// 2026-08-03 수정 - 이슈 #52: children에 render prop(close) 허용 — 액션 메뉴 지원.
// 파일 위치: frontend/src/components/common/Popover.tsx
//
// title 툴팁은 모바일·터치에서 뜨지 않고 줄바꿈·표를 담을 수 없다. 설명이 한 줄을
// 넘는 경우에 쓰는 클릭형 레이어.
//
// 레이어는 **body 로 포털**해 `position: fixed` 로 띄운다. 표처럼 `overflow-*`
// 컨테이너 안에서 absolute 로 두면 잘리거나 스크롤이 생긴다(실제 발생).
// 기존 공통 모달(`components/feedback/Modal.tsx`)도 같은 이유로 fixed 를 쓴다.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface Props {
  /** 클릭 대상 (배지·값 등) */
  trigger: ReactNode
  /**
   * 레이어 내용. 함수를 주면 `close`를 받는다 — 항목을 고르면 스스로 닫아야 하는
   * 액션 메뉴용이다(이슈 #52). 레이어는 `z-100`이라 그대로 두면 모달(`z-50`) 위에
   * 남는다.
   */
  children: ReactNode | ((close: () => void) => ReactNode)
  /** 레이어 폭 (기본 260px) */
  width?: number
  /** 트리거 접근성 라벨 */
  label?: string
}

const GAP = 6        // 트리거와 레이어 사이 여백
const MARGIN = 8     // 화면 가장자리 최소 여백

export default function Popover({ trigger, children, width = 260, label }: Props) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])

  /** 트리거 기준으로 레이어 위치를 잡는다 — 아래 우선, 공간 부족하면 위로 뒤집는다. */
  const place = useCallback(() => {
    const t = triggerRef.current?.getBoundingClientRect()
    if (!t) return
    const h = panelRef.current?.offsetHeight ?? 0

    const below = t.bottom + GAP
    const flip = h > 0 && below + h > window.innerHeight - MARGIN && t.top - GAP - h > MARGIN
    const top = flip ? t.top - GAP - h : below

    // 우측 정렬하되 화면 밖으로 나가지 않게 가둔다.
    const left = Math.min(
      Math.max(MARGIN, t.right - width),
      window.innerWidth - width - MARGIN,
    )
    setPos({ top, left })
  }, [width])

  // 레이어가 그려진 뒤 높이를 알 수 있으므로 layout 단계에서 재배치한다.
  useLayoutEffect(() => {
    if (open) place()
  }, [open, place])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    // 스크롤·리사이즈 시 트리거를 계속 따라가게 한다. 캡처 단계여야 내부
    // 스크롤 컨테이너(표 등)의 스크롤도 잡힌다.
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        className="cursor-pointer align-middle"
      >
        {trigger}
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          className="fixed z-[100] rounded border p-2.5 text-left text-[10px] leading-relaxed"
          style={{
            top: pos.top,
            left: pos.left,
            width,
            borderColor: 'var(--border-subtle)',
            background: 'var(--color-bg-panel)',
            color: 'var(--color-text-body)',
            boxShadow: 'var(--elev-2)',
          }}
        >
          {typeof children === 'function' ? children(close) : children}
        </div>,
        document.body,
      )}
    </>
  )
}
